import { ClaimWorkflowConfiguration } from '../../api';
import { stableStringify } from './stable-stringify';

/** A single workflow configuration option value. */
export type WorkflowConfigValue = boolean | number | string;

/** True when a configuration value is unset (missing from config or explicitly cleared). */
export function isUnsetConfigValue(value: WorkflowConfigValue | null | undefined): boolean {
    return value === null || value === undefined;
}

/** Configured values for one workflow status. */
export type WorkflowStatusConfig = Record<string, WorkflowConfigValue>;

/** Full workflow config keyed by status. */
export type WorkflowConfigMap = Record<string, WorkflowStatusConfig>;

/**
 * Claim workflow configuration with accurate value types.
 * The generated API model types all config values as boolean only.
 */
export interface WorkerWorkflowConfiguration {
    createDate?: string;
    active?: boolean;
    config?: WorkflowConfigMap;
}

/** Deep-clones a workflow config map. */
export function cloneWorkflowConfigMap(config?: WorkflowConfigMap): WorkflowConfigMap {
    return JSON.parse(JSON.stringify(config ?? {})) as WorkflowConfigMap;
}

/** Returns a new config map with one option set or cleared (removed when unset). */
export function setWorkflowConfigValue(
    config: WorkflowConfigMap,
    status: string,
    key: string,
    value: WorkflowConfigValue | null | undefined,
): WorkflowConfigMap {
    const next = cloneWorkflowConfigMap(config);

    if (!next[status]) {
        next[status] = {};
    }

    if (value === null || value === undefined) {
        delete next[status][key];
        if (Object.keys(next[status]).length === 0) {
            delete next[status];
        }
        return next;
    }

    next[status][key] = value;
    return next;
}

/** True when two workflow config maps are deeply equal. */
export function workflowConfigsEqual(left: WorkflowConfigMap, right: WorkflowConfigMap): boolean {
    return stableStringify(left) === stableStringify(right);
}

/** True when a status has at least one configured option value. */
export function statusHasConfiguredOptions(statusConfig?: WorkflowStatusConfig): boolean {
    return Boolean(statusConfig && Object.keys(statusConfig).length > 0);
}

/** Maps the API response to the locally accurate configuration shape. */
export function parseWorkerWorkflowConfiguration(
    config: ClaimWorkflowConfiguration,
): WorkerWorkflowConfiguration {
    return {
        createDate: config.createDate,
        active: config.active,
        config: config.config as unknown as Record<string, WorkflowStatusConfig> | undefined,
    };
}
